import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { homePathFor, isHomePath } from '../i18n/localePath';
import { legalPaths } from '../legal/routes';
import { PRODUCTS, productUrl } from '../products';
import { scrollToTopInstant } from './ScrollToTop';
import { scrollToSection } from '../utils/scrollToSection';

export const CONTACT_EMAIL = 'info@tamerin.tech';
export const CONTACT_PHONE_DISPLAY = '+49 8026 387 25 98';
export const CONTACT_PHONE_TEL = '+4980263872598';

/**
 * Footer: wordmark and address, active products and the footer-only Morgenbrot
 * link, company links, and the language-specific legal pages.
 */
const Footer: React.FC = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const paths = legalPaths[language];
  const homePath = homePathFor(language);
  const isHome = isHomePath(location.pathname);

  const handleSectionClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    if (isHome && scrollToSection(id)) {
      event.preventDefault();
    }
  };

  const productLinks = PRODUCTS.filter((product) => product.featured || product.status === 'live').map((product) => ({
    product,
    href: productUrl(product, language, 'footer_products')
  })).filter((entry): entry is { product: (typeof PRODUCTS)[number]; href: string } => Boolean(entry.href));

  return (
    <footer className="lp-footer lp-wrap" role="contentinfo">
      <div className="lp-footer-top">
        <div className="lp-footer-identity">
          <Link className="lp-footer-brand" to={homePath} onClick={scrollToTopInstant}>
            <img src="/logo.png" alt="TamerinTECH" width={174} height={42} loading="lazy" />
          </Link>
          <address className="lp-footer-address">
            TamerinTECH GmbH
            <br />
            Seestr. 19, 83727 Schliersee, {language === 'de' ? 'Deutschland' : 'Germany'}
            <br />
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            <br />
            <a href={`tel:${CONTACT_PHONE_TEL}`}>{CONTACT_PHONE_DISPLAY}</a>
          </address>
        </div>

        <nav className="lp-footer-col" aria-label={t.footer.columns.products}>
          <h2>{t.footer.columns.products}</h2>
          <ul>
            {productLinks.map(({ product, href }) => (
              <li key={product.id}>
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {product.shortName}
                </a>
              </li>
            ))}
            <li>
              <a href="https://www.morgen-brot.de/" target="_blank" rel="noopener noreferrer">
                Morgenbrot
              </a>
            </li>
          </ul>
        </nav>

        <nav className="lp-footer-col" aria-label={t.footer.columns.company}>
          <h2>{t.footer.columns.company}</h2>
          <ul>
            <li>
              <Link to={`${homePath}#about`} onClick={(event) => handleSectionClick(event, 'about')}>
                {t.footer.links.about}
              </Link>
            </li>
            <li>
              <Link to={`${homePath}#faq`} onClick={(event) => handleSectionClick(event, 'faq')}>
                {t.footer.links.faq}
              </Link>
            </li>
            <li>
              <Link to={`${homePath}#contact`} onClick={(event) => handleSectionClick(event, 'contact')}>
                {t.footer.links.contact}
              </Link>
            </li>
          </ul>
        </nav>

        <nav className="lp-footer-col lp-footer-legal" aria-label={t.footer.columns.legal}>
          <h2>{t.footer.columns.legal}</h2>
          <ul>
            <li>
              <Link to={paths.impressum} onClick={scrollToTopInstant}>
                {t.footer.legal.impressum}
              </Link>
            </li>
            <li>
              <Link to={paths.privacy} onClick={scrollToTopInstant}>
                {t.footer.legal.privacy}
              </Link>
            </li>
            <li>
              <Link to={paths.cookies} onClick={scrollToTopInstant}>
                {t.footer.legal.cookies}
              </Link>
            </li>
          </ul>
        </nav>
      </div>

      <div className="lp-footer-bottom">
        <span>{t.footer.copyright.replace('{year}', currentYear.toString())}</span>
        <span>{t.footer.privacyNote}</span>
      </div>
    </footer>
  );
};

export default Footer;
